import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  LucideAngularModule,
  ArrowRight,
  Loader2,
  Trophy,
  Target,
  Calendar,
  Clock,
  RotateCcw,
} from 'lucide-angular';
import { ConfettiCanvasComponent } from '../../../../../common/components/confetti-canvas/confetti-canvas';
import {
  SubmissionResult,
  STUDENT_ASSIGNMENT_THEME_COLOR,
  GRADING_PROGRESS_STEPS,
} from '../../services/student-assignment.models';

/** SVG circle constants for the score ring */
const RING_RADIUS = 52;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

@Component({
  selector: 'app-quiz-result',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, DatePipe, ConfettiCanvasComponent],
  templateUrl: './quiz-result.html',
  styleUrl: './quiz-result.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizResultComponent {
  @Input() result: SubmissionResult | null = null;
  @Input() isGrading = false;
  @Input() gradingProgressStep = 0;
  @Input() submittedAt: string | null = null;
  @Input() isTestMode = false;

  @Output() readonly continue = new EventEmitter<void>();
  @Output() readonly retest = new EventEmitter<void>();

  protected readonly themeColor = STUDENT_ASSIGNMENT_THEME_COLOR;
  protected readonly progressSteps = GRADING_PROGRESS_STEPS;
  protected readonly confettiColors = [
    '#22C55E',
    '#4ADE80',
    '#86EFAC',
    '#FACC15',
    '#FDE047',
  ];
  protected readonly ringCircumference = RING_CIRCUMFERENCE;

  protected readonly icons = {
    ArrowRight,
    Loader2,
    Trophy,
    Target,
    Calendar,
    Clock,
    RotateCcw,
  };

  protected get currentProgressLabel(): string {
    return this.progressSteps[this.gradingProgressStep]?.label ?? 'Processing...';
  }

  protected get gradePercentage(): number {
    return this.result?.gradePercentage ?? 0;
  }

  protected get pointsAwarded(): number {
    return this.result?.totalPointsAwarded ?? 0;
  }

  protected get totalPoints(): number {
    return this.result?.totalPointsPossible ?? 0;
  }

  /** Stroke-dashoffset for the SVG score ring */
  protected get ringOffset(): number {
    return RING_CIRCUMFERENCE * (1 - this.gradePercentage / 100);
  }

  protected get performanceLevel(): 'excellent' | 'good' | 'needs-improvement' {
    const percentage = this.gradePercentage;
    if (percentage >= 80) return 'excellent';
    if (percentage >= 60) return 'good';
    return 'needs-improvement';
  }

  /** Color for the score ring based on performance */
  protected get ringColor(): string {
    switch (this.performanceLevel) {
      case 'excellent':
        return '#22C55E';
      case 'good':
        return '#F59E0B';
      case 'needs-improvement':
        return '#EF4444';
    }
  }

  /** Light background tint for icons and accents */
  protected get ringColorLight(): string {
    switch (this.performanceLevel) {
      case 'excellent':
        return '#22C55E14';
      case 'good':
        return '#F59E0B14';
      case 'needs-improvement':
        return '#EF444414';
    }
  }

  protected get showConfetti(): boolean {
    return this.performanceLevel !== 'needs-improvement';
  }

  protected get confettiParticleCount(): number {
    return this.performanceLevel === 'excellent' ? 150 : 80;
  }

  protected get performanceEmoji(): string {
    switch (this.performanceLevel) {
      case 'excellent':
        return '🎉';
      case 'good':
        return '👍';
      case 'needs-improvement':
        return '😉';
    }
  }

  protected get performanceMessage(): string {
    switch (this.performanceLevel) {
      case 'excellent':
        return 'Excellent work! Great job!';
      case 'good':
        return 'Good work! Keep it up!';
      case 'needs-improvement':
        return 'Needs Improvement';
    }
  }

  protected get performanceDescription(): string {
    switch (this.performanceLevel) {
      case 'excellent':
        return 'You demonstrated a strong understanding of the material.';
      case 'good':
        return 'You showed good knowledge. Keep practicing!';
      case 'needs-improvement':
        return 'You can definitely prepare for the next task to achieve maximum results.';
    }
  }

  /** Human-readable duration between startedAt and submittedAt */
  protected get duration(): string | null {
    const start = this.result?.startedAt;
    const end = this.result?.submittedAt;
    if (!start || !end) return null;

    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms <= 0) return null;

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  protected onContinue(): void {
    this.continue.emit();
  }

  protected onRetest(): void {
    this.retest.emit();
  }
}
